import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import BackButton from '../../components/ui/BackNavigationButton';
import { useTranslation } from 'react-i18next';
import { 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Legend, 
  Tooltip, 
  XAxis, 
  YAxis, 
  CartesianGrid,
  LabelList,
} from 'recharts';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';

const ScheduleAnalysisPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { scheduleData, scheduleName } = location.state || {};
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [viewMode, setViewMode] = useState('weekly');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedEmployees, setSelectedEmployees] = useState(new Set());
  
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const isMobile = windowWidth < 768;
  
  useEffect(() => {
    if (!scheduleData?.schedule) {
      navigate('/schedules');
    }
  }, [navigate, scheduleData]);

  const shiftDistribution = useMemo(() => {
    return scheduleData.schedule.map(employee => {
      let morningShifts = 0;
      let afternoonShifts = 0;
      let nightShifts = 0;
      let offDays = 0;
      let absences = 0;
      let totalWorkingMinutes = 0;

      employee.shifts.forEach(shift => {
        if (!shift) return;

        const shiftCode = shift.shift || '';
        const shiftType = shift.type || '';
        
        if (shiftCode.includes('(M') || shiftType === 'M') {
          morningShifts++;
          if (shift.startTime !== undefined && shift.endTime !== undefined) {
            totalWorkingMinutes += (shift.endTime - shift.startTime) * 60;
          }
        } else if (shiftCode.includes('(T') || shiftType === 'T') {
          afternoonShifts++;
          if (shift.startTime !== undefined && shift.endTime !== undefined) {
            totalWorkingMinutes += (shift.endTime - shift.startTime) * 60;
          }
        } else if (shiftCode.includes('(N') || shiftType === 'N') {
          nightShifts++;
          if (shift.startTime !== undefined && shift.endTime !== undefined) {
            totalWorkingMinutes += (shift.endTime - shift.startTime) * 60;
          }
        } else if (shiftType === 'Off' || shiftCode === 'FF') {
          offDays++;
        } else if (shiftType === 'Absence' || shiftCode === 'Abs' || shiftCode === 'FE' || shiftCode === 'FR') {
          absences++;
        }
      });

      const totalHours = totalWorkingMinutes / 60;
      
      return {
        name: employee.name || employee.employee,
        morningShifts,
        afternoonShifts,
        nightShifts,
        offDays,
        absences,
        totalHours,
        totalMinutes: totalWorkingMinutes,
        expectedHours: employee.expectedHours || 40 
      };
    });
  }, [scheduleData]);

  const filteredAndSortedEmployees = useMemo(() => {
    let filtered = shiftDistribution.filter(employee =>
      employee.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    filtered.sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  }, [shiftDistribution, searchTerm, sortBy, sortOrder]);

  const displayEmployees = useMemo(() => {
    if (selectedEmployees.size > 0) {
      return filteredAndSortedEmployees.filter(employee => 
        selectedEmployees.has(employee.name)
      );
    }
    return filteredAndSortedEmployees;
  }, [filteredAndSortedEmployees, selectedEmployees]);

  const absenceAnalysis = useMemo(() => {
    if (!scheduleData?.schedule?.length) return { weekly: [], monthly: [], totalWeeks: 0, totalMonths: 0 };
    
    const totalWeeks = Math.ceil(scheduleData.schedule[0].shifts.length / 7);
    
    const weeklyAbsenceData = [];
    for (let week = 0; week < totalWeeks; week++) {
      let weekAbsences = 0;
      let weekTotalShifts = 0;
      
      scheduleData.schedule.forEach(employee => {
        for (let day = 0; day < 7; day++) {
          const shiftIndex = week * 7 + day;
          if (shiftIndex < employee.shifts.length) {
            weekTotalShifts++;
            
            const shift = employee.shifts[shiftIndex];
            if (shift) {
              const shiftCode = shift.shift || '';
              const shiftType = shift.type || '';
              
              if (shiftType === 'Absence' || shiftCode === 'Abs' || 
                  shiftCode === 'FE' || shiftCode === 'FR') {
                weekAbsences++;
              }
            }
          }
        }
      });
      
      const weeklyPercentage = weekTotalShifts > 0 ? (weekAbsences / weekTotalShifts) * 100 : 0;
      weeklyAbsenceData.push({
        weekNumber: week + 1,
        absencePercentage: weeklyPercentage,
        absenceCount: weekAbsences,
        attendanceCount: weekTotalShifts - weekAbsences,
        totalShifts: weekTotalShifts,
        absenceData: [
          { name: t('analysis.absences') || 'Absences', value: weekAbsences },
          { name: t('analysis.attendance') || 'Attendance', value: weekTotalShifts - weekAbsences }
        ]
      });
    }
    
    const WEEKS_PER_MONTH = 4;
    const totalMonths = Math.ceil(totalWeeks / WEEKS_PER_MONTH);
    const monthlyAbsenceData = [];
    
    for (let month = 0; month < totalMonths; month++) {
      let monthAbsences = 0;
      let monthTotalShifts = 0;
      
      const startWeek = month * WEEKS_PER_MONTH;
      const endWeek = Math.min((month + 1) * WEEKS_PER_MONTH, totalWeeks);
      
      for (let week = startWeek; week < endWeek; week++) {
        const weekData = weeklyAbsenceData[week];
        monthAbsences += weekData.absenceCount;
        monthTotalShifts += weekData.totalShifts;
      }
      
      const monthlyPercentage = monthTotalShifts > 0 ? (monthAbsences / monthTotalShifts) * 100 : 0;
      
      monthlyAbsenceData.push({
        monthNumber: month + 1,
        absencePercentage: monthlyPercentage,
        absenceCount: monthAbsences,
        attendanceCount: monthTotalShifts - monthAbsences,
        totalShifts: monthTotalShifts,
        absenceData: [
          { name: t('analysis.absences') || 'Absences', value: monthAbsences },
          { name: t('analysis.attendance') || 'Attendance', value: monthTotalShifts - monthAbsences }
        ],
        weeksInMonth: endWeek - startWeek
      });
    }
    
    const totalShifts = scheduleData.schedule.reduce((sum, employee) => {
      return sum + (employee.shifts.length || 0);
    }, 0);

    const totalAbsences = shiftDistribution.reduce((sum, employee) => {
      return sum + employee.absences;
    }, 0);

    const overallAbsencePercentage = totalShifts > 0 ? (totalAbsences / totalShifts) * 100 : 0;
    
    return {
      weekly: weeklyAbsenceData,
      monthly: monthlyAbsenceData,
      overall: [
        { name: t('analysis.absences') || 'Absences', value: totalAbsences },
        { name: t('analysis.attendance') || 'Attendance', value: totalShifts - totalAbsences }
      ],
      totalWeeks,
      totalMonths
    };
  }, [scheduleData, shiftDistribution, t]);

  const formatHoursToTimeString = (hours) => {
    const wholeHours = Math.floor(hours);
    const minutes = Math.floor((hours - wholeHours) * 60);
    return `${wholeHours}:${minutes.toString().padStart(2, '0')}h`;
  };

  const ShiftTypeDistributionChart = () => {
    const [currentPage, setCurrentPage] = useState(0);
    const employeesPerPage = 12;

    const chartData = displayEmployees.map(employee => ({
      name: employee.name.length > 12 ? `${employee.name.substring(0, 12)}...` : employee.name,
      fullName: employee.name,
      morningShifts: employee.morningShifts,
      afternoonShifts: employee.afternoonShifts,
      nightShifts: employee.nightShifts,
      totalShifts: employee.morningShifts + employee.afternoonShifts + employee.nightShifts
    }));

    const totalPages = Math.ceil(chartData.length / employeesPerPage);
    const startIndex = currentPage * employeesPerPage;
    const endIndex = startIndex + employeesPerPage;
    const currentPageData = chartData.slice(startIndex, endIndex);

    const goToPage = (page) => {
      setCurrentPage(page);
    };

    const goToPrevious = () => {
      setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const goToNext = () => {
      setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    return (
      <div className="space-y-6">
        
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-green-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {t("analysis.showing")} {startIndex + 1}-{Math.min(endIndex, chartData.length)} {t("analysis.of")} {chartData.length} {t("analysis.employees")}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevious}
                disabled={currentPage === 0}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← {t("analysis.previous")}
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => goToPage(i)}
                    className={`w-8 h-8 text-sm rounded-md transition-colors ${
                      currentPage === i
                        ? 'bg-green-600 text-white'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              
              <button
                onClick={goToNext}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("analysis.next")} →
              </button>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t("analysis.page")} {currentPage + 1} {t("analysis.of")} {totalPages} • {employeesPerPage} {t("analysis.perPage")}
            </p>
          </div>
        )}

        <div className="h-56 md:h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={currentPageData}
              margin={{ top: 60, right: 20, left: 10, bottom: isMobile ? 80 : 60 }}
            >
              <XAxis
                dataKey="name"
                angle={isMobile ? -45 : -30}
                textAnchor="end"
                tick={{ fontSize: isMobile ? 9 : 10 }}
                interval={0}
                height={isMobile ? 80 : 60}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#4B5563' }}
                tickLine={false}
                axisLine={{ stroke: '#E5E7EB' }}
                label={{ 
                  value: t('analysis.numberOfShifts') || 'Number of Shifts', 
                  angle: -90, 
                  position: 'insideLeft', 
                  style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 12, fontWeight: 500 } 
                }}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#FFF', 
                  border: 'none', 
                  borderRadius: '6px', 
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                  padding: '8px 12px',
                  fontSize: '12px'
                }}
                cursor={{ fill: 'rgba(229, 231, 235, 0.4)' }}
                labelFormatter={(label) => {
                  const employee = currentPageData.find(emp => emp.name === label);
                  return employee ? employee.fullName : label;
                }}
              />
              
              <Bar 
                dataKey="morningShifts" 
                name={t('analysis.morningShifts') || 'Morning Shifts'} 
                fill="#3B82F6"
                radius={[4, 4, 0, 0]}
              >
                <LabelList 
                  dataKey="morningShifts"
                  position="top" 
                  style={{ fontSize: '10px', fill: '#4B5563', fontWeight: 'bold' }}
                  formatter={(value) => (value > 0 ? value : '')}
                />
              </Bar>
              <Bar 
                dataKey="afternoonShifts" 
                name={t('analysis.afternoonShifts') || 'Afternoon Shifts'} 
                fill="#10B981"
                radius={[4, 4, 0, 0]}
              >
                <LabelList 
                  dataKey="afternoonShifts" 
                  position="top"
                  style={{ fontSize: '10px', fill: '#4B5563', fontWeight: 'bold' }}
                  formatter={(value) => (value > 0 ? value : '')}
                />
              </Bar>
              <Bar 
                dataKey="nightShifts" 
                name={t('analysis.nightShifts') || 'Night Shifts'} 
                fill="#F59E0B"
                radius={[4, 4, 0, 0]}
              >
                <LabelList 
                  dataKey="nightShifts" 
                  position="top"
                  style={{ fontSize: '10px', fill: '#4B5563', fontWeight: 'bold' }}
                  formatter={(value) => (value > 0 ? value : '')}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 flex flex-col md:flex-row justify-center items-start md:items-center gap-3 md:gap-6">
          {[
            { color: '#3B82F6', label: t('analysis.morningShifts') || 'Morning Shifts' },
            { color: '#10B981', label: t('analysis.afternoonShifts') || 'Afternoon Shifts' },
            { color: '#F59E0B', label: t('analysis.nightShifts') || 'Night Shifts' }
          ].map((item, idx) => (
            <div key={idx} className="flex items-center">
              <span className="w-3 h-3 md:w-4 md:h-4 rounded-full mr-2" style={{ backgroundColor: item.color }} />
              <span className="text-xs md:text-sm text-gray-600">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const getMonthDateRange = (monthIndex) => {
    if (!absenceAnalysis.monthly || !absenceAnalysis.monthly[monthIndex - 1]) {
      return '';
    }
    
    const WEEKS_PER_MONTH = 4;
    const startWeek = (monthIndex - 1) * WEEKS_PER_MONTH + 1;
    const endWeek = startWeek + absenceAnalysis.monthly[monthIndex - 1].weeksInMonth - 1;
    
    return `${t('analysis.weeks') || 'Weeks'} ${startWeek}-${endWeek}`;
  };

  const getCurrentWeekData = () => {
    return absenceAnalysis.weekly[selectedWeek - 1] || { absenceData: [{ value: 0 }, { value: 0 }], totalShifts: 0 };
  };

  const getCurrentMonthData = () => {
    return absenceAnalysis.monthly[selectedMonth - 1] || { absenceData: [{ value: 0 }, { value: 0 }], totalShifts: 0 };
  };

  const renderPieChartLabel = ({ name, value }) => {
    if (!isMobile) {
      return `${name}: ${value}`;
    }
    return null;
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleEmployeeSelect = (employeeName) => {
    const newSelected = new Set(selectedEmployees);
    if (newSelected.has(employeeName)) {
      newSelected.delete(employeeName);
    } else {
      newSelected.add(employeeName);
    }
    setSelectedEmployees(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedEmployees.size === filteredAndSortedEmployees.length && filteredAndSortedEmployees.length > 0) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(filteredAndSortedEmployees.map(emp => emp.name)));
    }
  };

  const handleClearSelection = () => {
    setSelectedEmployees(new Set());
  };

  const ExpectedVsPlannedChart = () => {
    const totalWeeks = scheduleData?.schedule?.[0]?.shifts ? 
      Math.ceil(scheduleData.schedule[0].shifts.length / 7) : 1;

    const [currentPage, setCurrentPage] = useState(0);
    const employeesPerPage = 15;

    const comparisonData = displayEmployees.map(employee => {
      const weeklyExpectedHours = employee.expectedHours;
      const totalExpectedHours = weeklyExpectedHours * totalWeeks;
      
      return {
        name: employee.name.length > 10 ? `${employee.name.substring(0, 10)}...` : employee.name,
        fullName: employee.name,
        expected: totalExpectedHours,
        planned: employee.totalHours,
        difference: employee.totalHours - totalExpectedHours,
        percentageDifference: totalExpectedHours > 0 ? ((employee.totalHours - totalExpectedHours) / totalExpectedHours) * 100 : 0,
        weeklyExpected: weeklyExpectedHours
      };
    });

    const totalPages = Math.ceil(comparisonData.length / employeesPerPage);
    const startIndex = currentPage * employeesPerPage;
    const endIndex = startIndex + employeesPerPage;
    const finalData = comparisonData.slice(startIndex, endIndex);

    const CustomTooltip = ({ active, payload, label }) => {
      if (active && payload && payload.length) {
        const data = payload[0].payload;
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-semibold text-gray-900 mb-2">{data.fullName}</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">{t("analysis.expectedWeeklyHours")}</span>
                <span className="font-medium">{formatHoursToTimeString(data.weeklyExpected)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">{t("analysis.expectedTotalHours")}</span>
                <span className="font-medium">{formatHoursToTimeString(data.expected)}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-600">{t("analysis.plannedTotalHours")}</span>
                <span className="font-medium">{formatHoursToTimeString(data.planned)}</span>
              </div>
              
            </div>
          </div>
        );
      }
      return null;
    };

    const goToPage = (page) => {
      setCurrentPage(page);
    };

    const goToPrevious = () => {
      setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const goToNext = () => {
      setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    return (
      <div className="space-y-6">
        {totalPages > 1 && (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {t("analysis.showing")} {startIndex + 1}-{Math.min(endIndex, comparisonData.length)} {t("analysis.of")} {comparisonData.length} {t("analysis.employees")}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevious}
                disabled={currentPage === 0}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← {t("analysis.previous")}
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => goToPage(i)}
                    className={`w-8 h-8 text-sm rounded-md transition-colors ${
                      currentPage === i
                        ? 'bg-blue-600 text-white'
                        : 'bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              
              <button
                onClick={goToNext}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("analysis.next")} →
              </button>
            </div>
          </div>
        )}

        {totalPages > 1 && (
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t("analysis.page")} {currentPage + 1} {t("analysis.of")} {totalPages} • {employeesPerPage} {t("analysis.perPage")}
            </p>
          </div>
        )}
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={finalData}
              margin={{ top: 20, right: 30, left: 20, bottom: isMobile ? 100 : 70 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis 
                dataKey="name"
                angle={isMobile ? -45 : -30}
                textAnchor="end"
                height={isMobile ? 100 : 70}
                tick={{ fontSize: isMobile ? 10 : 12, fill: '#4B5563' }}
                interval={0}
              />
              <YAxis 
                tick={{ fontSize: 12, fill: '#4B5563' }}
                label={{ 
                  value: t('analysis.hours') || 'Hours', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                verticalAlign="top"
                height={36}
                iconType="rect"
              />
              
              <Bar 
                dataKey="expected" 
                name={`${t("analysis.expectedHours")} (${totalWeeks} ${t("analysis.weeks")})`} 
                fill="#94A3B8" 
                radius={[2, 2, 0, 0]}
                opacity={0.8}
              />
              <Bar 
                dataKey="planned" 
                name={`${t("analysis.plannedTotal")}`}
                fill="#3B82F6" 
                radius={[2, 2, 0, 0]}
              >
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen -mt-16">
      <div className="max-w-7xl mx-auto">
        <BackButton onClick={() => navigate(-1)} />
        
        <div className="mt-4 mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            {t('analysis.title') || 'Schedule Analysis'}: {scheduleName}
          </h1>
          <p className="text-sm md:text-base text-gray-600 mt-2">
            {t('analysis.description') || 'Detailed analysis of shift distribution and working hours.'}
          </p>
        </div>

        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder={t('analysis.searchEmployees') || 'Search employees...'}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-wrap items-center gap-3">
                  <button
                    onClick={handleSelectAll}
                    className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    {selectedEmployees.size === filteredAndSortedEmployees.length && filteredAndSortedEmployees.length > 0 ? t("analysis.deselectAll") : t("analysis.selectAll")}
                  </button>
                  
                  {selectedEmployees.size > 0 && (
                    <button
                      onClick={handleClearSelection}
                      className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      {t("analysis.clearSelection")}
                    </button>
                  )}
                </div>
                
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span>
                    {selectedEmployees.size > 0 ? (
                      <>
                        {selectedEmployees.size} {t("analysis.of")} {filteredAndSortedEmployees.length} {t("analysis.selected")}
                        <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                          {t("analysis.showingInCharts") || "Showing in charts"}
                        </span>
                      </>
                    ) : (
                      <>
                        {filteredAndSortedEmployees.length} {t("analysis.employees")} 
                        <span className="ml-2 px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                          {t("analysis.showingAll") || "Showing all"}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6">
          <CardContent className="p-4 md:p-6">
            <h2 className="text-lg md:text-xl font-semibold mb-3">
              {t('analysis.shiftDistribution') || 'Shift Distribution per Employee'}
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-2 py-2 md:px-4 md:py-3">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.size === filteredAndSortedEmployees.length && filteredAndSortedEmployees.length > 0}
                        onChange={handleSelectAll}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </th>
                    {[
                      { key: 'name', label: t('analysis.employee') || 'Employee' },
                      { key: 'morningShifts', label: t('analysis.morningShifts') || 'Morning Shifts' },
                      { key: 'afternoonShifts', label: t('analysis.afternoonShifts') || 'Afternoon Shifts' },
                      { key: 'nightShifts', label: t('analysis.nightShifts') || 'Night Shifts' },
                      { key: 'offDays', label: t('analysis.offDays') || 'Off Days' },
                      { key: 'absences', label: t('analysis.absences') || 'Absences' },
                      { key: 'totalHours', label: t('analysis.totalHours') || 'Total Hours' }
                    ].map((header) => (
                      <th 
                        key={header.key}
                        className="px-2 py-2 md:px-4 md:py-3 text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider text-center cursor-pointer hover:bg-gray-100"
                        onClick={() => handleSort(header.key)}
                      >
                        <div className="flex items-center justify-center gap-1">
                          {header.label}
                          <span className="text-gray-400">{getSortIcon(header.key)}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredAndSortedEmployees.map((employee, index) => (
                    <tr 
                      key={index} 
                      className={`hover:bg-gray-50 ${
                        selectedEmployees.has(employee.name) ? 'bg-blue-50' : ''
                      }`}
                    >
                      <td className="px-2 py-2 md:px-4 md:py-3 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedEmployees.has(employee.name)}
                          onChange={() => handleEmployeeSelect(employee.name)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-2 py-2 md:px-4 md:py-3 whitespace-nowrap text-xs md:text-sm font-medium text-gray-900">
                        {employee.name}
                      </td>
                      {[
                        employee.morningShifts,
                        employee.afternoonShifts,
                        employee.nightShifts,
                        employee.offDays,
                        employee.absences,
                        formatHoursToTimeString(employee.totalHours)
                      ].map((value, idx) => (
                        <td 
                          key={idx}
                          className="px-2 py-2 md:px-4 md:py-3 whitespace-nowrap text-xs md:text-sm text-gray-600 text-center"
                        >
                          {value}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">
              {t('analysis.expectedVsPlannedHours') || 'Expected vs Planned Total Hours'}
            </h2>
            <p className="text-gray-600 mb-4">
              {t('analysis.hoursComparisonDescription') || `This chart compares the expected total working hours for each employee (weekly expected × ${Math.ceil((scheduleData?.schedule?.[0]?.shifts?.length || 0) / 7)} weeks) with their actual planned hours in the schedule.`}
            </p>
            <ExpectedVsPlannedChart />
            
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold">
                  {t('analysis.absencePercentage') || 'Absence Analysis'}
                </h2>
                
                {absenceAnalysis.totalWeeks > 4 && (
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setViewMode('weekly')}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        viewMode === 'weekly' 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {t('analysis.weekly') || 'Weekly'}
                    </button>
                    <button 
                      onClick={() => setViewMode('monthly')}
                      className={`px-3 py-1 text-sm rounded-lg ${
                        viewMode === 'monthly' 
                          ? 'bg-indigo-600 text-white' 
                          : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      {t('analysis.monthly') || 'Monthly'}
                    </button>
                  </div>
                )}
              </div>
              
              {viewMode === 'weekly' ? (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                      {t('analysis.week') || 'Week'} {selectedWeek}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setSelectedWeek(prev => Math.max(1, prev - 1))}
                        disabled={selectedWeek <= 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => setSelectedWeek(prev => Math.min(absenceAnalysis.totalWeeks, prev + 1))}
                        disabled={selectedWeek >= absenceAnalysis.totalWeeks}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getCurrentWeekData().absenceData}
                          cx="50%"
                          cy="50%"
                          labelLine={!isMobile}
                          label={renderPieChartLabel}
                          outerRadius={isMobile ? 60 : 80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          <Cell fill="#FF8042" />
                          <Cell fill="#0088FE" />
                        </Pie>
                        <Tooltip formatter={(value) => [value, '']} />
                        <Legend 
                          layout={isMobile ? "horizontal" : "vertical"}
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">
                      {t('analysis.month') || 'Month'} {selectedMonth} - {getMonthDateRange(selectedMonth)}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button 
                        onClick={() => setSelectedMonth(prev => Math.max(1, prev - 1))}
                        disabled={selectedMonth <= 1}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      <button 
                        onClick={() => setSelectedMonth(prev => Math.min(absenceAnalysis.totalMonths, prev + 1))}
                        disabled={selectedMonth >= absenceAnalysis.totalMonths}
                        className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
                      >
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={getCurrentMonthData().absenceData}
                          cx="50%"
                          cy="50%"
                          labelLine={!isMobile}
                          label={renderPieChartLabel}
                          outerRadius={isMobile ? 60 : 80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          <Cell fill="#FF8042" />
                          <Cell fill="#0088FE" />
                        </Pie>
                        <Tooltip formatter={(value) => [value, '']} />
                        <Legend 
                          layout={isMobile ? "horizontal" : "vertical"}
                          verticalAlign="bottom"
                          align="center"
                          wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4">
                {t('analysis.shiftTypeDistribution') || 'Shift Type Distribution'}
              </h2>
              <ShiftTypeDistributionChart />
              
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ScheduleAnalysisPage;